import React, { useState } from "react";
import { AdminApis } from "../../apis/adminApi";
import { Link, NavLink } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import ImageUploading from "react-images-uploading";
import { useNavigate } from "react-router-dom";
import { IoIosAddCircle } from "react-icons/io";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiCamera, FiX, FiCheckCircle, FiSearch, FiPlus, FiGlobe,
  FiFileText, FiExternalLink, FiAlertTriangle, FiLayers, FiTrash2,
} from "react-icons/fi";

export default function CardCreateMultiLink() {
  const navigate = useNavigate();

  /* ── Image upload ─────────────────────────────────────────────── */
  const [images, setImages] = useState([]);
  const onChange = (imageList) => setImages(imageList);

  /* ── Form state ───────────────────────────────────────────────── */
  const [name, setName] = useState("");
  const [nameExist, setNameExist] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");
  const [businessSite, setBusinessSite] = useState("");
  const [businessPolicy, setBusinessPolicy] = useState("");
  const [addlink, setAddLink] = useState("");
  const [permissionList, setPermissionList] = useState([]);   // selected link names
  const [availableLinks, setAvailableLinks] = useState([]);   // all user's WA links
  const [loader, setLoader] = useState(false);

  const cleanName = (n) => n.toLowerCase().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");

  /* ── Fetch available links ────────────────────────────────────── */
  React.useEffect(() => {
    AdminApis.getlinks().then((res) => {
      if (res?.data) setAvailableLinks(res.data.link || []);
    });
  }, []);

  /* ── Name availability check ──────────────────────────────────── */
  React.useEffect(() => {
    if (!name) return;
    AdminApis.searchName({ name: cleanName(name) }).then((res) => {
      if (res?.data) setNameExist(res.data.data);
    });
  }, [name]);

  /* ── Add / Remove links ───────────────────────────────────────── */
  const addLinkToList = (value) => {
    if (!value || permissionList.includes(value)) return;
    setPermissionList((prev) => [...prev, value]);
  };

  const removeLinkFromList = (index) => {
    setPermissionList((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── Submit ───────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (permissionList.length < 2) {
      toast.error("Add at least 2 WhatsApp links to your multi-link page.");
      return;
    }
    setLoader(true);
    const fd = new FormData();
    fd.append("name", cleanName(name));
    fd.append("title", title);
    fd.append("bio", bio);
    fd.append("attach_links", permissionList.toString());
    fd.append("logo", images.length ? images[0].file : "No selected file");
    fd.append("redirect_link", addlink);
    fd.append("business_website", businessSite || "https://www.mygupta.co");
    fd.append("business_policy", businessPolicy || "no policy");
    try {
      const res = await AdminApis.createTieredLink(fd);
      if (res?.data) {
        toast.success(res.data.message);
        navigate("/multi-links");
      } else {
        toast.error(res?.response?.data?.message || "Failed to create page.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoader(false);
    }
  };

  const hasEnoughLinks = availableLinks.length >= 2;
  const canSubmit = !loader && hasEnoughLinks && cleanName(name).length > 0 && nameExist !== 1 && permissionList.length >= 2;
  const logoSrc = images.length ? images[0].data_url : null;

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50 pb-20">

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0071BC]/10 flex items-center justify-center">
              <FiLayers className="text-[#0071BC]" size={17} />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">Create Multi-Link Page</h1>
              <p className="text-xs text-gray-400 mt-0.5">Combine all your WhatsApp numbers into one shareable page</p>
            </div>
          </div>
          <NavLink to="/multi-links">
            <button className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors">← Back</button>
          </NavLink>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 pt-6">

        {/* ── Not enough links banner ──────────────────────────────── */}
        {!hasEnoughLinks && (
          <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4">
            <FiAlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">You need at least 2 WhatsApp links</p>
              <p className="text-xs text-amber-700 mt-1">Create your WhatsApp links first, then come back to build your multi-link page.</p>
            </div>
            <NavLink to="/mylinks">
              <button className="flex-shrink-0 px-4 py-2 bg-amber-500 text-white text-xs font-bold rounded-xl hover:bg-amber-600 active:scale-95 transition-all">
                Create Links
              </button>
            </NavLink>
          </div>
        )}

        {/* ── Main two-column layout ───────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── LEFT: Editor ──────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Logo upload */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Profile Image</p>
              <ImageUploading multiple value={images} onChange={onChange} maxNumber={1} dataURLKey="data_url">
                {({ imageList, onImageUpload, onImageUpdate, onImageRemove }) => (
                  <div className="flex items-center gap-5">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#0071BC] transition-colors"
                        onClick={imageList.length ? undefined : onImageUpload}
                      >
                        {imageList.length ? (
                          <img src={imageList[0].data_url} alt="logo" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <FiCamera className="text-gray-400" size={22} />
                            <span className="text-[10px] text-gray-400 font-medium">Upload</span>
                          </div>
                        )}
                      </div>
                      {imageList.length > 0 && (
                        <button type="button" onClick={() => onImageRemove(0)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors">
                          <FiX size={10} strokeWidth={3} />
                        </button>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-700 mb-1">Brand Logo</p>
                      <p className="text-xs text-gray-400 mb-3">Square image, max 1 MB. Shown at top of your page.</p>
                      {imageList.length ? (
                        <button type="button" onClick={() => onImageUpdate(0)}
                          className="px-3 py-1.5 text-xs font-bold text-[#0071BC] bg-[#0071BC]/10 rounded-xl hover:bg-[#0071BC]/20 transition-colors">
                          Change Image
                        </button>
                      ) : (
                        <button type="button" onClick={onImageUpload}
                          className="px-3 py-1.5 text-xs font-bold text-[#0071BC] bg-[#0071BC]/10 rounded-xl hover:bg-[#0071BC]/20 transition-colors">
                          Choose Image
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </ImageUploading>
            </div>

            {/* Page details */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Page Details</p>

              {/* Brand name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Page URL Name <span className="text-gray-400 font-normal">(Keep it short)</span>
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="text"
                    required
                    placeholder="Eg. mark-shoe-links"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-gray-400">mygupta.co/<strong className="text-gray-600">{cleanName(name) || "your-name"}</strong></span>
                  {name && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${nameExist > 0 ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}>
                      {nameExist > 0 ? <FiX size={9} /> : <FiCheckCircle size={9} />}
                      {nameExist > 0 ? "Name taken" : "Available"}
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Title</label>
                <input
                  type="text"
                  placeholder="Your business or brand name"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Bio</label>
                <textarea
                  rows={3}
                  placeholder="A short description of your business…"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all leading-relaxed"
                />
              </div>
            </div>

            {/* WhatsApp links */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">WhatsApp Links</p>
                <Link to="/mylinks" className="flex items-center gap-1 text-xs font-bold text-[#0071BC] hover:opacity-80 transition-opacity">
                  <IoIosAddCircle size={16} /> Add More
                </Link>
              </div>

              {availableLinks.length === 0 ? (
                <div className="text-center py-4">
                  <FaWhatsapp className="text-gray-200 mx-auto mb-2" size={28} />
                  <p className="text-sm text-gray-400">No WhatsApp links yet.</p>
                  <NavLink to="/mylinks">
                    <button type="button" className="mt-2 text-xs font-bold text-[#0071BC] hover:underline">Create your first link →</button>
                  </NavLink>
                </div>
              ) : (
                <>
                  {/* Dropdown */}
                  <div className="relative">
                    <select
                      value=""
                      onChange={(e) => { addLinkToList(e.target.value); setAddLink(e.target.value); }}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all cursor-pointer"
                    >
                      <option value="">Select a link to add…</option>
                      {availableLinks.map((l) => (
                        <option key={l.id} value={l.name} disabled={permissionList.includes(l.name)}>
                          {l.name}{permissionList.includes(l.name) ? " ✓" : ""}
                        </option>
                      ))}
                    </select>
                    <FiPlus className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                  </div>

                  {/* Requirement note */}
                  <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-xl ${permissionList.length >= 2 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                    {permissionList.length >= 2
                      ? <><FiCheckCircle size={12} /> {permissionList.length} links selected — ready to publish</>
                      : <><FiAlertTriangle size={12} /> Select at least {2 - permissionList.length} more link{2 - permissionList.length > 1 ? "s" : ""} to publish</>
                    }
                  </div>

                  {/* Selected links */}
                  {permissionList.length > 0 && (
                    <div className="space-y-2">
                      {permissionList.map((linkName, index) => (
                        <div key={index} className="flex items-center gap-3 bg-[#0071BC]/5 border border-[#0071BC]/15 rounded-2xl px-4 py-3">
                          <div className="w-7 h-7 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                            <FaWhatsapp className="text-white" size={13} />
                          </div>
                          <span className="flex-1 text-sm font-semibold text-gray-700 truncate">{linkName}</span>
                          <button
                            type="button"
                            onClick={() => removeLinkFromList(index)}
                            className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                          >
                            <FiX size={12} strokeWidth={2.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Optional details */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Optional Details</p>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Business Website</label>
                <div className="relative">
                  <FiGlobe className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="url"
                    placeholder="https://yourwebsite.com"
                    value={businessSite}
                    onChange={(e) => setBusinessSite(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Business Policy URL</label>
                <div className="relative">
                  <FiFileText className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                  <input
                    type="text"
                    placeholder="https://yourpolicy.com"
                    value={businessPolicy}
                    onChange={(e) => setBusinessPolicy(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-4 text-white font-extrabold text-sm rounded-2xl transition-all shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: canSubmit ? "#0071BC" : "#9ca3af" }}
            >
              {loader ? "Publishing…" : !hasEnoughLinks ? "Create 2+ WhatsApp Links First" : permissionList.length < 2 ? `Select ${2 - permissionList.length} More Link${2 - permissionList.length > 1 ? "s" : ""}` : "Publish Multi-Link Page"}
            </button>
          </form>

          {/* ── RIGHT: Live Preview ────────────────────────────────── */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

              {/* Preview header */}
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-1 text-[11px] text-gray-400 font-medium">
                    mygupta.co/{cleanName(name) || "your-page"}
                  </div>
                </div>
              </div>

              {/* Page preview */}
              <div className="px-8 py-10 flex flex-col items-center text-center min-h-[480px]"
                style={{ background: "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)" }}>

                {/* Logo */}
                <div className="relative mb-4">
                  <div className="w-20 h-20 rounded-2xl bg-white border-2 border-gray-200 shadow-md overflow-hidden flex items-center justify-center">
                    {logoSrc ? (
                      <img src={logoSrc} alt="logo" className="w-full h-full object-cover" />
                    ) : (
                      <FiCamera className="text-gray-300" size={28} />
                    )}
                  </div>
                </div>

                {/* Title + bio */}
                <h2 className="text-xl font-extrabold text-gray-900 mb-1">{title || <span className="text-gray-300">Your Title</span>}</h2>
                <p className="text-sm text-gray-500 mb-6 max-w-[240px] leading-relaxed">{bio || <span className="text-gray-300 italic">Your bio appears here</span>}</p>

                {/* WhatsApp link buttons */}
                {permissionList.length > 0 ? (
                  <div className="w-full space-y-3 max-w-xs">
                    {permissionList.map((linkName, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#0071BC] text-white rounded-2xl px-5 py-3 shadow-md">
                        <FaWhatsapp size={16} className="flex-shrink-0" />
                        <span className="flex-1 text-sm font-bold truncate">{linkName}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-4">
                    <div className="w-10 h-10 rounded-2xl bg-gray-100 flex items-center justify-center">
                      <FaWhatsapp className="text-gray-300" size={20} />
                    </div>
                    <p className="text-xs text-gray-300 italic">Your WhatsApp links will appear here</p>
                  </div>
                )}

                {/* More info */}
                {businessSite && (
                  <div className="mt-6 pt-5 border-t border-gray-200 w-full max-w-xs">
                    <p className="text-xs text-gray-400 mb-2">More Info</p>
                    <a href={businessSite} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0071BC] text-[#0071BC] text-sm font-bold rounded-2xl hover:bg-[#0071BC]/5 transition-colors">
                      Visit <FiExternalLink size={13} />
                    </a>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-gray-200 w-full max-w-xs">
                  <p className="text-xs text-gray-300 font-medium">Powered by Gupta</p>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-2xl p-4">
              <p className="text-xs font-bold text-[#0071BC] mb-2">💡 Tips</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li>• Add at least 2 WhatsApp links to publish</li>
                <li>• Use a square logo for best results</li>
                <li>• Keep your title and bio short and clear</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
